import {HttpClient, HttpContext, HttpHeaders, HttpParams, HttpResponse} from '@angular/common/http';
import {instanceToPlain} from 'class-transformer';
import {ApiResponse} from './api-response/api-response.model';
import {BaseIdModel} from './models/base-id.model';
import {map} from 'rxjs/operators';
import {Observable} from 'rxjs';
import {inject} from "@angular/core";
import {HTTP_STATUS_HANDLING, HttpStatusHandling} from "./mes-http-status-handler.service";

interface IApiParameters {
  action?: string;
  controller?: string;
  area?: string;
  id?: string;
  handleResponse?: boolean;
  query?: {};
  headers?: HttpHeaders | {
    [header: string]: string | string[];
  }
}

export abstract class MesApiService {
  protected http = inject(HttpClient);

  protected constructor(public controller: string, private area?: string) {
  }

  public getList<T extends BaseIdModel | any>(params: IApiParameters, query?: any, handling?: HttpStatusHandling): Observable<ApiResponse<T[]>> {
    query = query ?? params?.query ?? {};
    query.id = query.id ?? params?.id;

    query = this.prepareParams(query);
    return this.http.get<ApiResponse<T[]>>(this.getRoute(params), {params: query, headers: params.headers, context: this.getHandlingContext(handling)});
  }

  public getSingle<T extends BaseIdModel | any>(paramsOrId: IApiParameters | string, query?: any, handling?: HttpStatusHandling): Observable<ApiResponse<T>> {
    let params: IApiParameters = {};
    if (typeof paramsOrId === 'string') {
      params.id = paramsOrId;
    } else {
      Object.assign(params, paramsOrId);
    }
    params = this.ensureParamsAndAction(params, 'Get');
    return this.http.get<ApiResponse<T>>(this.getRoute(params, params.id), {params: this.prepareParams(query), headers: this.prepareHeaders(params), context: this.getHandlingContext(handling)}).pipe(map(value => {
      return new ApiResponse<T>(value);
    }));
  }

  public add<T extends BaseIdModel | any>(value: T, params?: IApiParameters): Observable<ApiResponse<T>> {
    return this.addGet<T, T>(value, params);
  }

  public addGet<T, TK extends BaseIdModel | any>(value: T, params?: IApiParameters, handling?: HttpStatusHandling): Observable<ApiResponse<TK>> {
    value = this.prepareDate(value);
    const toAdd = JSON.stringify(instanceToPlain(value));
    params = this.ensureParamsAndAction(params, 'Post');
    return this.http.post<ApiResponse<TK>>(this.getRoute(this.ensureParamsAndAction(params, 'Get')), toAdd, {headers: this.prepareHeaders(params), context: this.getHandlingContext(handling)})
      .pipe(map(value1 => {
        return new ApiResponse<TK>(value1);
      }));
  }

  public update<T extends BaseIdModel | any>(id: string, itemToUpdate: any, params?: IApiParameters, handling?: HttpStatusHandling): Observable<ApiResponse<T>> {
    itemToUpdate = this.prepareDate(itemToUpdate);
    const object = JSON.stringify(instanceToPlain(itemToUpdate));
    params = this.ensureParamsAndAction(params, 'Put');
    return this.http.put<ApiResponse<T>>(this.getRoute(params, id), object, {headers: this.prepareHeaders(params), context: this.getHandlingContext(handling)}).pipe(map(value => {
      return new ApiResponse<T>(value);
    }));
  }

  public updateGet<T extends BaseIdModel | any, TK extends BaseIdModel | any>(id: string, itemToUpdate: any, params?: IApiParameters, handling?: HttpStatusHandling): Observable<ApiResponse<TK>> {
    itemToUpdate = this.prepareDate(itemToUpdate);
    const object = JSON.stringify(instanceToPlain(itemToUpdate));
    params = this.ensureParamsAndAction(params, 'Put');
    return this.http.put<ApiResponse<TK>>(this.getRoute(params, id), object, {headers: this.prepareHeaders(params), context: this.getHandlingContext(handling)}).pipe(map(value => {
      return new ApiResponse<TK>(value);
    }));
  }

  public delete<T extends BaseIdModel | any>(id: string, params?: IApiParameters, handling?: HttpStatusHandling): Observable<ApiResponse<T>> {
    params = this.ensureParamsAndAction(params, 'Delete');
    return this.http.delete<ApiResponse<T>>(this.getRoute(params, id), {headers: this.prepareHeaders(params), context: this.getHandlingContext(handling)}).pipe(map(value => {
      return new ApiResponse<T>(value);
    }));
  }

  protected getRoute(params: IApiParameters, ...args) {
    return ['api', params.area || this.area, params.controller || this.controller, params.action, ...args]
      .filter(value => value != null).join('/');
  }

  private prepareParams(query: any) {
    let searchParams: HttpParams = new HttpParams();
    for (const key in query) {
      if (query.hasOwnProperty(key)) {
        const val = query[key];
        if (val instanceof Date && !isNaN(val.valueOf())) {
          searchParams = searchParams.append(key, this.prepareDate(val as Date).toISOString());
        } else if (Array.isArray(val)) {
          val.map(value => {
            searchParams = searchParams.append(key, value);
          });
        } else if (val != null) {
          searchParams = searchParams.append(key, val);
        }
      }
    }
    return searchParams;
  }

  private ensureParamsAndAction(params: IApiParameters, action: string) {
    params = params || {};
    params.action = params.action || action;
    return params;
  }

  private prepareDate(body: any) {
    if (body === null || body === undefined) {
      return body;
    }
    if (body instanceof Date) {
      let value = body;
      body = new Date(value.getFullYear(), value.getMonth(), value.getDate(), value.getHours(), value.getMinutes(), value.getSeconds());
      //console.log('Change date:' + value.toISOString() + ' -> ' + body.toISOString())
    }
    if (typeof body !== 'object') {
      return body;
    }

    for (const key of Object.keys(body)) {
      const value = body[key];
      this.prepareDate(value);
    }
    return body;
  }

  private prepareHeaders(params: IApiParameters): HttpHeaders {
    if (params.handleResponse == null) {
      params.handleResponse = true;
    }
    let httpHeaders = new HttpHeaders();

    if (params.headers instanceof HttpHeaders) {
      const headers: HttpHeaders = params.headers;
      headers.keys().forEach(key => {
        httpHeaders = httpHeaders.set(key, headers.get(key) || '');
      });
    } else {
      const headers = params.headers as { [header: string]: string };
      for (const key in headers) {
        if (headers.hasOwnProperty(key)) {
          httpHeaders = httpHeaders.set(key, headers[key]);
        }
      }
    }

    httpHeaders.set('handleResponse', params.handleResponse.toString());
    return httpHeaders;
  }

  protected getFileNameAndData(response: HttpResponse<Blob>) {
    return {
      fileName: (response.headers.get('Content-Disposition').match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)[1]).replace(/['"]+/g, '').replace(/_$/, ''),
      data: response.body
    }
  }

  private getHandlingContext(handling: HttpStatusHandling) {
    handling = new HttpStatusHandling(handling);
    return new HttpContext().set(HTTP_STATUS_HANDLING, handling);
  }
}
